import * as React from 'react';
import { api } from './api';
import { monthOptions, formatMoney, normalizeDetail, MonthlyAnalyticsCharts, computeExpenseAnalytics, ExpenseTypeSummaryChart, ExpenseTypeMonthlyChart, computeYearlyTable, computePieData, computeOverspendBars, computeHeatmapData } from './shared';
import { usePlannerActions } from './PlannerActionContext';

function YearlyPieChart({ pieData, currencyCode }) {
  const { pieData: data, totalActual } = pieData;
  const total = totalActual || 0;
  if (!data || data.length === 0) {
    return (
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Годовой план-факт</p>
            <h2>Структура расходов за год</h2>
          </div>
        </div>
        <p className="empty-state">Нет расходов за выбранный период.</p>
      </section>
    );
  }
  let gradient = '';
  let angle = 0;
  for (const seg of data) {
    if (seg.percentage === 0) continue;
    const start = angle;
    const end = angle + seg.percentage * 3.6;
    gradient += `${seg.color} ${start}deg ${end}deg, `;
    angle = end;
  }
  gradient = gradient.replace(/, $/, '') || '#e8eef0';

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Годовой план-факт</p>
          <h2>Структура расходов за год</h2>
        </div>
      </div>
      <div className="pie-chart-wrapper">
        <div className="donut-chart" style={{ background: `conic-gradient(${gradient})` }}>
          <div className="donut-center">
            <strong>{formatMoney(total, currencyCode)}</strong>
            <span>Всего расходов</span>
          </div>
        </div>
        <div className="pie-legend">
          {data.map((d) => (
            <div key={d.item_id || d.name} className="pie-legend-item">
              <span className="pie-legend-dot" style={{ background: d.color }} />
              <span className="pie-legend-label">{d.name}</span>
              <span className="pie-legend-amount">{formatMoney(d.actual, currencyCode)}</span>
              <span className="pie-legend-pct">({d.percentage.toFixed(1)}%)</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function YearlySectionTable({ kind, title, showAvg, summaries, currencyCode }) {
  const { rows, totalPlanned, totalActual } = computeYearlyTable(summaries, kind);
  if (rows.length === 0) {
    return (
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Годовой план-факт</p>
            <h2>{title}</h2>
          </div>
        </div>
        <p className="empty-state">Нет данных по данной категории.</p>
      </section>
    );
  }
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Годовой план-факт</p>
          <h2>{title}</h2>
        </div>
        {showAvg && (
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.45 }}>
            <strong>Средний (акт. месяцы)</strong> — сумма расходов делится на число месяцев, в которых были фактические траты.<br />
            <strong>Средний / 12</strong> — сумма расходов делится на 12 (полный год), включая месяцы без трат.
          </p>
        )}
      </div>
      <div className="table-scroll">
        <table className="planner-table">
          <thead>
            <tr>
              <th>Статья</th>
              <th>Запланировано</th>
              <th>Потрачено суммарно</th>
              {showAvg && <th title="Сумма расходов / число месяцев с тратами">Средний (акт. месяцы)</th>}
              {showAvg && <th title="Сумма расходов / 12 (полный год)">Средний / 12</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td>{row.name}</td>
                <td>{formatMoney(row.planned, currencyCode)}</td>
                <td>{formatMoney(row.actual, currencyCode)}</td>
                {showAvg && <td>{formatMoney(row.avgPayment, currencyCode)}</td>}
                {showAvg && <td>{formatMoney(row.avgPayment12, currencyCode)}</td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="planfact-total-row">
              <td><strong>Итого</strong></td>
              <td><strong>{formatMoney(totalPlanned, currencyCode)}</strong></td>
              <td><strong>{formatMoney(totalActual, currencyCode)}</strong></td>
              {showAvg && <td />}
              {showAvg && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function YearlyPlanFactBars({ barData, currencyCode }) {
  const { rows, maxValue } = barData;
  if (!rows || rows.length === 0) return null;
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Годовой план-факт</p>
          <h2>Перерасход по статьям</h2>
        </div>
      </div>
      <div className="planfact-bars">
        {rows.map((row, idx) => {
          const overrun = row.actual > row.planned;
          return (
            <div key={idx} className="planfact-bar-row">
              <span className="planfact-bar-label">{row.name}</span>
              <div className="planfact-bar-tracks">
                <div className="planfact-bar-track">
                  <span
                    className="planfact-bar-plan"
                    style={{ width: `${(row.planned / maxValue) * 100}%` }}
                  />
                </div>
                <div className="planfact-bar-track">
                  <span
                    className={`planfact-bar-actual${overrun ? ' planfact-bar-risk' : ''}`}
                    style={{ width: `${(row.actual / maxValue) * 100}%` }}
                  />
                </div>
              </div>
              <div className="planfact-bar-values">
                <span className="planfact-bar-plan-val">{formatMoney(row.planned, currencyCode)}</span>
                <span className={overrun ? 'risk-value' : ''}>{formatMoney(row.actual, currencyCode)}</span>
                <span className={overrun ? 'risk-value' : 'good-value'}>
                  {overrun ? '+' : ''}{formatMoney(row.variance, currencyCode)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function YearlyHeatmap({ heatmapData, currencyCode }) {
  const { items, maxOverspend } = heatmapData;
  if (!items || items.length === 0) return null;
  const monthLabels = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Годовой план-факт</p>
          <h2>Перерасход по месяцам</h2>
        </div>
      </div>
      <div className="heatmap-container">
        <div className="heatmap-grid">
          <div className="heatmap-header heatmap-corner">Статья</div>
          {monthLabels.map((label) => (
            <div key={label} className="heatmap-header">{label}</div>
          ))}
          {items.map((item, idx) => (
            <React.Fragment key={idx}>
              <div className="heatmap-label">{item.name}</div>
              {item.months.map((m) => {
                const intensity = maxOverspend > 0 ? (m.overspend / maxOverspend) * 100 : 0;
                return (
                  <div
                    key={m.month}
                    className={`heatmap-cell${m.overspend > 0 ? ' heatmap-cell-active' : ''}`}
                    style={m.overspend > 0 ? {
                      backgroundColor: `color-mix(in srgb, #fef3c7, #dc2626 ${intensity}%)`,
                    } : {}}
                    title={`${item.name} — ${monthLabels[m.month - 1]}: ${formatMoney(m.overspend, currencyCode)}`}
                  >
                    {m.overspend > 0 ? formatMoney(m.overspend, currencyCode) : ''}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="heatmap-legend">
          <span className="heatmap-legend-label">Нет перерасхода</span>
          <span className="heatmap-legend-bar" />
          <span className="heatmap-legend-label">Максимальный</span>
        </div>
      </div>
    </section>
  );
}

function YearlyPlanFactDashboard({ summaries, currencyCode }) {
  const expenseSummaries = summaries.filter(
    (s) => s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit'
  );
  if (expenseSummaries.length === 0) return null;

  const sectionConfigs = [
    { kind: 'recurring', title: 'Периодические расходы', showAvg: true },
    { kind: 'one_time', title: 'Единоразовые расходы', showAvg: false },
    { kind: 'limit', title: 'Лимиты на расходы', showAvg: true },
  ];

  const pieData = computePieData(summaries);
  const barData = computeOverspendBars(summaries);
  const heatmapData = computeHeatmapData(summaries);

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Годовой план-факт</p>
            <h2>Аналитика расходов за год</h2>
          </div>
        </div>
      </section>

      <YearlyPieChart pieData={pieData} currencyCode={currencyCode} />

      {sectionConfigs.map((cfg) => (
        <YearlySectionTable
          key={cfg.kind}
          kind={cfg.kind}
          title={cfg.title}
          showAvg={cfg.showAvg}
          summaries={summaries}
          currencyCode={currencyCode}
        />
      ))}

      <YearlyPlanFactBars barData={barData} currencyCode={currencyCode} />

      <YearlyHeatmap heatmapData={heatmapData} currencyCode={currencyCode} />
    </>
  );
}

export default function AnalyticsWorkspace() {
  const { selectedYear, setSelectedYear, budgets, selectedBudgetId } = usePlannerActions();
  const [draft, setDraft] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [incomeUserFilter, setIncomeUserFilter] = React.useState('');
  const [selectedMonth, setSelectedMonth] = React.useState(null);

  React.useEffect(() => {
    if (!selectedBudgetId) {
      setDraft(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function loadBudget() {
      try {
        setLoading(true);
        const detail = await api.getPlannerBudget(selectedBudgetId);
        if (!cancelled) {
          setDraft(normalizeDetail(detail));
          setError('');
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadBudget();
    return () => {
      cancelled = true;
    };
  }, [selectedBudgetId]);

  if (loading) {
    return (
      <main className="planner-page">
        <section className="hero-card">
          <p className="eyebrow">Analytics</p>
          <h1>Загружаю аналитику...</h1>
        </section>
      </main>
    );
  }

  return (
    <main className="planner-page">
      <section className="hero-card">
        <div>
          <p className="eyebrow">Analytics</p>
          <h1>Аналитика бюджета</h1>
          <p className="hero-copy">
            Помесячный план-факт анализ доходов и расходов по выбранному бюджету.
          </p>
        </div>
        <div className="hero-actions">
          <label className="field compact-field">
            <span>Год</span>
            <select value={selectedYear} onChange={(event) => setSelectedYear(Number(event.target.value))}>
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.year}>
                  {budget.year}
                </option>
              ))}
            </select>
          </label>
          <label className="field compact-field">
            <span>Месяц</span>
            <select value={selectedMonth || ''} onChange={(event) => setSelectedMonth(event.target.value ? Number(event.target.value) : null)}>
              <option value="">Все месяцы</option>
              {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
        </div>
      </section>

      {error && (
        <section className="message-banner error">{error}</section>
      )}

      {draft && (
        <>
          <IncomeMonthlyAnalytics
            summaries={draft.item_summaries || []}
            userFilter={incomeUserFilter}
            onUserFilterChange={setIncomeUserFilter}
            selectedMonth={selectedMonth}
          />

          <ExpenseAnalytics
            summaries={draft.item_summaries || []}
            currencyCode={draft.budget.currency_code}
            selectedMonth={selectedMonth}
          />

          <PlanFactExpensesTable
            summaries={draft.item_summaries || []}
            selectedMonth={selectedMonth}
          />

          <section className="panel">
            <div className="panel-header">
              <div>
                <p className="panel-kicker">Plan-Fact</p>
                <h2>Отклонения по месяцам</h2>
              </div>
            </div>
            <MonthlyAnalyticsCharts rows={filterRowsByMonth(draft.monthly_plan || [], selectedMonth)} />
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <p className="panel-kicker">Plan-Fact</p>
                <h2>Помесячная аналитика</h2>
              </div>
            </div>
            <MonthlyPlanTable rows={draft.monthly_plan || []} selectedMonth={selectedMonth} />
          </section>

          {selectedMonth == null && draft && (
            <YearlyPlanFactDashboard
              summaries={draft.item_summaries || []}
              currencyCode={draft.budget.currency_code}
            />
          )}
        </>
      )}

      {!draft && !loading && (
        <section className="panel">
          <p className="empty-state">Выберите бюджет для просмотра аналитики.</p>
        </section>
      )}
    </main>
  );
}

function filterRowsByMonth(rows, selectedMonth) {
  if (selectedMonth == null) {
    return rows;
  }
  return rows.filter((row) => row.month === selectedMonth);
}

function ExpenseAnalytics({ summaries, currencyCode = 'RUB', selectedMonth = null }) {
  const expenseSummaries = summaries.filter(
    (s) => s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit',
  );
  if (expenseSummaries.length === 0) {
    return null;
  }
  const { typeTotals, monthlyRows, typeLabels } = computeExpenseAnalytics(summaries, selectedMonth);

  const monthLabel = selectedMonth != null
    ? monthOptions.find((m) => m.value === selectedMonth)?.label
    : null;

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Аналитика расходов</p>
            <h2>Сводка по типам расходов{monthLabel ? ` — ${monthLabel}` : ''}</h2>
          </div>
        </div>
        <ExpenseTypeSummaryChart typeTotals={typeTotals} typeLabels={typeLabels} currencyCode={currencyCode} />
      </section>

      {selectedMonth == null && (
        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Аналитика расходов</p>
              <h2>Помесячная детализация по типам</h2>
            </div>
          </div>
          <ExpenseTypeMonthlyChart monthlyRows={monthlyRows} typeLabels={typeLabels} currencyCode={currencyCode} />
          <div className="chart-legend">
            <span><i className="legend-dot expense-plan" /> План</span>
            <span><i className="legend-dot expense-actual" /> Факт</span>
            <span><i className="legend-dot legend-recurring" /> Периодические</span>
            <span><i className="legend-dot legend-onetime" /> Единоразовые</span>
            <span><i className="legend-dot legend-limit" /> Лимиты</span>
          </div>
        </section>
      )}
    </>
  );
}

function IncomeMonthlyAnalytics({ summaries, userFilter, onUserFilterChange, selectedMonth }) {
  const incomeRows = summaries.filter((item) => item.kind === 'income');
  if (incomeRows.length === 0) {
    return null;
  }

  const owners = [...new Set(incomeRows.map((item) => item.income_owner).filter(Boolean))].sort();

  let filtered = incomeRows;
  if (userFilter) {
    filtered = incomeRows.filter((item) => item.income_owner === userFilter);
  }

  const hasBonus = filtered.some((item) => item.income_type === 'bonus');
  const typeLabels = { salary: 'Зарплата', bonus: 'Премия', other: 'Другое' };

  const tableRows = [];
  for (const item of filtered) {
    const breakout = item.monthly_breakout || [];
    for (const mb of breakout) {
      if (selectedMonth != null && mb.month !== selectedMonth) {
        continue;
      }
      // Skip rows where both planned and actual are zero — avoids clutter
      // for one-time items (bonus, other) that only apply to specific months.
      if (Number(mb.planned || 0) === 0 && Number(mb.actual || 0) === 0) {
        continue;
      }
      tableRows.push({
        month: mb.month,
        monthLabel: monthOptions.find((m) => m.value === mb.month)?.label,
        user: item.income_owner || 'Не указан',
        name: item.name,
        type: item.income_type || 'other',
        planned: mb.planned || 0,
        actual: mb.actual || 0,
        variance: (mb.actual || 0) - (mb.planned || 0),
      });
    }
  }

  tableRows.sort((a, b) => a.month - b.month || a.user.localeCompare(b.user) || a.name.localeCompare(b.name));

  // Insert subtotal rows after each month group so the user can see
  // aggregate planned/actual per month at a glance.
  const rowsWithSubtotals = [];
  let currentMonth = null;
  let monthSubtotal = { planned: 0, actual: 0, variance: 0 };
  const pushSubtotal = (month, subtotal) => {
    rowsWithSubtotals.push({
      isSubtotal: true,
      month,
      monthLabel: monthOptions.find((m) => m.value === month)?.label,
      planned: subtotal.planned,
      actual: subtotal.actual,
      variance: subtotal.variance,
    });
  };
  for (let i = 0; i < tableRows.length; i++) {
    const row = tableRows[i];
    if (currentMonth !== null && row.month !== currentMonth) {
      pushSubtotal(currentMonth, monthSubtotal);
      monthSubtotal = { planned: 0, actual: 0, variance: 0 };
    }
    currentMonth = row.month;
    monthSubtotal.planned += Number(row.planned);
    monthSubtotal.actual += Number(row.actual);
    monthSubtotal.variance += Number(row.variance);
    rowsWithSubtotals.push(row);
  }
  if (currentMonth !== null) {
    pushSubtotal(currentMonth, monthSubtotal);
  }

  const yearlyTotals = filtered.reduce(
    (acc, item) => {
      for (const mb of item.monthly_breakout || []) {
        acc.planned += Number(mb.planned || 0);
        acc.actual += Number(mb.actual || 0);
      }
      return acc;
    },
    { planned: 0, actual: 0 },
  );
  yearlyTotals.variance = yearlyTotals.actual - yearlyTotals.planned;

  return (
    <section className="panel income-summary-panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Доходы</p>
          <h2>План-факт по месяцам</h2>
        </div>
        <div className="panel-actions">
          <label className="field compact-field">
            <span>Фильтр по пользователю</span>
            <select value={userFilter} onChange={(event) => onUserFilterChange(event.target.value)}>
              <option value="">Все пользователи</option>
              {owners.map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {hasBonus && (
        <div className="income-info-banner">
          Премия показывается отдельной строкой в выбранном месяце. Ее плановая сумма выделяется из общего фактического поступления на тот же счет, а остаток относится к зарплате.
        </div>
      )}
      <div className="table-scroll">
        <table className="planner-table income-summary-table">
          <thead>
            <tr>
              <th>Месяц</th>
              <th>Пользователь</th>
              <th>Статья</th>
              <th>Тип</th>
              <th>План</th>
              <th>Факт</th>
              <th>Отклонение</th>
            </tr>
          </thead>
          <tbody>
            {rowsWithSubtotals.map((row, idx) => {
              if (row.isSubtotal) {
                const lag = Number(row.actual) < Number(row.planned);
                return (
                  <tr key={`subtotal-${row.month}`} className={`income-subtotal-row${lag ? ' row-income-lag' : ''}`}>
                    <td colSpan={4}><strong>Итого за {row.monthLabel || row.month}</strong></td>
                    <td><strong>{formatMoney(row.planned)}</strong></td>
                    <td className={lag ? 'risk-value' : ''}><strong>{formatMoney(row.actual)}</strong></td>
                    <td className={lag ? 'risk-value' : ''}>
                      <strong>{formatMoney(row.variance)}</strong>
                      {lag && <span className="risk-pill">Ниже плана</span>}
                    </td>
                  </tr>
                );
              }
              const lag = Number(row.actual) < Number(row.planned);
              return (
                <tr key={`${row.month}-${row.user}-${row.name}-${idx}`} className={lag ? 'row-income-lag' : ''}>
                  <td>{row.monthLabel || row.month}</td>
                  <td>{row.user}</td>
                  <td>{row.name}</td>
                  <td>{typeLabels[row.type] || 'Другое'}</td>
                  <td>{formatMoney(row.planned)}</td>
                  <td className={lag ? 'risk-value' : ''}>{formatMoney(row.actual)}</td>
                  <td className={lag ? 'risk-value' : ''}>
                    {formatMoney(row.variance)} {lag && <span className="risk-pill">Ниже плана</span>}
                  </td>
                </tr>
              );
            })}
            {rowsWithSubtotals.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-cell">
                  Нет данных по доходам для выбранного фильтра.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="planfact-total-row">
              <td colSpan={4}><strong>Итого за год</strong></td>
              <td><strong>{formatMoney(yearlyTotals.planned)}</strong></td>
              <td className={yearlyTotals.actual < yearlyTotals.planned ? 'risk-value' : ''}>
                <strong>{formatMoney(yearlyTotals.actual)}</strong>
              </td>
              <td className={yearlyTotals.variance < 0 ? 'risk-value' : ''}>
                <strong>{formatMoney(yearlyTotals.variance)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function MonthlyPlanTable({ rows, selectedMonth }) {
  const filtered = selectedMonth != null ? rows.filter((row) => row.month === selectedMonth) : rows;
  if (!filtered.length) {
    return <p className="empty-state">После сохранения здесь появится помесячная аналитика.</p>;
  }

  const yearlyTotals = rows.reduce(
    (acc, row) => ({
      planned_income: acc.planned_income + Number(row.planned_income || 0),
      planned_expenses: acc.planned_expenses + Number(row.planned_expenses || 0),
      planned_balance: acc.planned_balance + Number(row.planned_balance || 0),
      actual_income: acc.actual_income + Number(row.actual_income || 0),
      actual_expenses: acc.actual_expenses + Number(row.actual_expenses || 0),
      actual_balance: acc.actual_balance + Number(row.actual_balance || 0),
      balance_variance: acc.balance_variance + Number(row.balance_variance || 0),
    }),
    { planned_income: 0, planned_expenses: 0, planned_balance: 0, actual_income: 0, actual_expenses: 0, actual_balance: 0, balance_variance: 0 },
  );

  return (
    <div className="table-scroll">
      <table className="planner-table">
        <thead>
          <tr>
            <th>Месяц</th>
            <th>План доход</th>
            <th>План расход</th>
            <th>План баланс</th>
            <th>Факт доход</th>
            <th>Факт расход</th>
            <th>Факт баланс</th>
            <th>Отклонение</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => {
            const expenseOverrun = Number(row.actual_expenses || 0) > Number(row.planned_expenses || 0);
            const incomeLag = Number(row.actual_income || 0) < Number(row.planned_income || 0);
            const balanceOverrun = Number(row.balance_variance || 0) < 0;
            return (
              <tr
                key={row.month}
                className={[
                  expenseOverrun ? 'row-expense-overrun' : '',
                  incomeLag ? 'row-income-lag' : '',
                  balanceOverrun ? 'row-balance-risk' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <td>
                  {monthOptions.find((month) => month.value === row.month)?.label || row.label}
                  {(expenseOverrun || incomeLag) && <span className="risk-pill">Вне плана</span>}
                </td>
                <td>{formatMoney(row.planned_income)}</td>
                <td>{formatMoney(row.planned_expenses)}</td>
                <td>{formatMoney(row.planned_balance)}</td>
                <td className={incomeLag ? 'risk-value' : ''}>{formatMoney(row.actual_income)}</td>
                <td className={expenseOverrun ? 'risk-value' : ''}>{formatMoney(row.actual_expenses)}</td>
                <td>{formatMoney(row.actual_balance)}</td>
                <td className={balanceOverrun ? 'risk-value' : 'good-value'}>{formatMoney(row.balance_variance)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="planfact-total-row">
            <td><strong>Итого за год</strong></td>
            <td><strong>{formatMoney(yearlyTotals.planned_income)}</strong></td>
            <td><strong>{formatMoney(yearlyTotals.planned_expenses)}</strong></td>
            <td><strong>{formatMoney(yearlyTotals.planned_balance)}</strong></td>
            <td className={yearlyTotals.actual_income < yearlyTotals.planned_income ? 'risk-value' : ''}>
              <strong>{formatMoney(yearlyTotals.actual_income)}</strong>
            </td>
            <td className={yearlyTotals.actual_expenses > yearlyTotals.planned_expenses ? 'risk-value' : ''}>
              <strong>{formatMoney(yearlyTotals.actual_expenses)}</strong>
            </td>
            <td><strong>{formatMoney(yearlyTotals.actual_balance)}</strong></td>
            <td className={yearlyTotals.balance_variance < 0 ? 'risk-value' : 'good-value'}>
              <strong>{formatMoney(yearlyTotals.balance_variance)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PlanFactExpensesTable({ summaries, selectedMonth }) {
  const sectionConfigs = [
    { kind: 'recurring', title: 'Периодические расходы' },
    { kind: 'one_time', title: 'Единоразовые расходы' },
    { kind: 'limit', title: 'Лимиты на расходы' },
  ];

  if (selectedMonth == null) {
    return (
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Plan-Fact</p>
            <h2>План-факт расходы</h2>
          </div>
        </div>
        <p className="empty-state">Выберите месяц для просмотра плана-факта расходов.</p>
      </section>
    );
  }

  const monthLabel = monthOptions.find((m) => m.value === selectedMonth)?.label || selectedMonth;

  const rows = [];
  for (const section of sectionConfigs) {
    const sectionItems = summaries.filter((s) => s.kind === section.kind);
    const sectionRows = [];
    for (const item of sectionItems) {
      const breakout = item.monthly_breakout || [];
      for (const mb of breakout) {
        if (mb.month !== selectedMonth) {
          continue;
        }
        if (Number(mb.planned || 0) === 0 && Number(mb.actual || 0) === 0) {
          continue;
        }
        sectionRows.push({
          name: item.name,
          planned: mb.planned || 0,
          actual: mb.actual || 0,
          variance: (mb.actual || 0) - (mb.planned || 0),
        });
      }
    }

    sectionRows.sort((a, b) => a.name.localeCompare(b.name));

    if (sectionRows.length === 0) {
      continue;
    }
    for (let i = 0; i < sectionRows.length; i++) {
      rows.push({
        ...sectionRows[i],
        sectionTitle: i === 0 ? section.title : null,
        sectionRowSpan: i === 0 ? sectionRows.length : null,
        isSectionFirst: i === 0,
        isEmpty: false,
      });
    }
  }

  const totals = rows.filter((r) => !r.isEmpty).reduce(
    (acc, r) => ({
      planned: acc.planned + Number(r.planned),
      actual: acc.actual + Number(r.actual),
      variance: acc.variance + Number(r.variance),
    }),
    { planned: 0, actual: 0, variance: 0 },
  );

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="panel-kicker">Plan-Fact</p>
          <h2>План-факт расходы &mdash; {monthLabel}</h2>
        </div>
      </div>
      <div className="table-scroll">
        <table className="planner-table planfact-table">
          <thead>
            <tr>
              <th className="section-col">Раздел</th>
              <th>Статья</th>
              <th>План</th>
              <th>Факт</th>
              <th>Отклонение</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const overrun = Number(row.actual) > Number(row.planned);
              return (
                <tr key={idx} className={overrun && !row.isEmpty ? 'row-expense-overrun' : ''}>
                  {row.isSectionFirst && (
                    <td
                      rowSpan={row.sectionRowSpan}
                      className={`section-cell${row.isEmpty ? ' section-cell-empty' : ''}`}
                    >
                      {row.sectionTitle}
                    </td>
                  )}
                  <td className={row.isEmpty ? 'empty-cell' : ''}>{row.name}</td>
                  <td>{row.isEmpty ? '' : formatMoney(row.planned)}</td>
                  <td className={overrun && !row.isEmpty ? 'risk-value' : ''}>
                    {row.isEmpty ? '' : formatMoney(row.actual)}
                  </td>
                  <td className={overrun && !row.isEmpty ? 'risk-value' : ''}>
                    {row.isEmpty ? '' : formatMoney(row.variance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.some((r) => !r.isEmpty) && (
            <tfoot>
              <tr className="planfact-total-row">
                <td colSpan={2}>Итого</td>
                <td>{formatMoney(totals.planned)}</td>
                <td className={totals.actual > totals.planned ? 'risk-value' : ''}>
                  {formatMoney(totals.actual)}
                </td>
                <td className={totals.variance > 0 ? 'risk-value' : 'good-value'}>
                  {formatMoney(totals.variance)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}
